import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Hotel, Bus, Plane, Users, Search, Check, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';

export type SelectedAllocation = {
  id: string;
  allocationId: string;
  name: string;
  allocationType: string;
  partnerId: string;
  partnerName: string;
  units: number;
  unitCost: number;
  currency: string;
  serviceDate: string;
  endDate?: string;
  availableUnits: number;
  fromPackage?: boolean;
  dayOffset?: number;
};

interface AllocationPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  travelStartDate: string;
  travelEndDate: string;
  existingAllocationIds?: string[];
  onSelect: (allocations: SelectedAllocation[]) => void;
}

type AllocationType = 'HOTEL_ROOM' | 'TOUR_SEAT' | 'TRANSFER_VEHICLE' | 'FLIGHT_SEAT';

const typeIcons: Record<AllocationType, typeof Hotel> = {
  HOTEL_ROOM: Hotel,
  TOUR_SEAT: Users,
  TRANSFER_VEHICLE: Bus,
  FLIGHT_SEAT: Plane,
};

const typeColors: Record<AllocationType, string> = {
  HOTEL_ROOM: 'bg-blue-100 text-blue-700',
  TOUR_SEAT: 'bg-purple-100 text-purple-700',
  TRANSFER_VEHICLE: 'bg-green-100 text-green-700',
  FLIGHT_SEAT: 'bg-sky-100 text-sky-700',
};

const typeLabels: Record<AllocationType, string> = {
  HOTEL_ROOM: 'Hotel',
  TOUR_SEAT: 'Tour',
  TRANSFER_VEHICLE: 'Transfer',
  FLIGHT_SEAT: 'Flight',
};

export function AllocationPickerModal({
  open,
  onOpenChange,
  travelStartDate,
  travelEndDate,
  existingAllocationIds = [],
  onSelect,
}: AllocationPickerModalProps) {
  const { t } = useTranslation('allocations');
  const { t: tCommon } = useTranslation('common');
  const { t: tBookings } = useTranslation('bookings');

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setSearch('');
      setTypeFilter('');
    }
  }, [open]);

  // Query available allocations
  const { data, isLoading } = trpc.allocation.list.useQuery(
    {
      page: 1,
      search: search || undefined,
      status: 'ACTIVE',
      hasAvailability: true,
      allocationType: typeFilter as AllocationType | undefined,
      validFrom: travelStartDate ? new Date(travelStartDate) : undefined,
      validTo: travelEndDate ? new Date(travelEndDate) : undefined,
    },
    {
      enabled: open,
    }
  );

  const allocations = (data?.allocations ?? []).filter(
    (a) => !existingAllocationIds.includes(a.id)
  );

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === allocations.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allocations.map((a) => a.id)));
    }
  };

  const handleConfirm = () => {
    const selectedAllocations: SelectedAllocation[] = allocations
      .filter((a) => selectedIds.has(a.id))
      .map((a) => ({
        id: crypto.randomUUID(),
        allocationId: a.id,
        name: a.name,
        allocationType: a.allocationType,
        partnerId: a.partner.id,
        partnerName: a.partner.companyName,
        units: 1,
        unitCost: a.unitCost,
        currency: a.currency,
        serviceDate: travelStartDate,
        availableUnits: a.availableUnits,
        fromPackage: false,
      }));

    onSelect(selectedAllocations);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {tBookings('allocations.addAllocations') || 'Add Allocations'}
          </DialogTitle>
          <DialogDescription>
            {tBookings('allocations.selectMultiple') ||
              'Select one or more allocations to add to this booking'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Filters */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('searchPlaceholder') || 'Search allocations...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Combobox
              options={[
                { value: '', label: 'All Types' },
                { value: 'HOTEL_ROOM', label: 'Hotel' },
                { value: 'TOUR_SEAT', label: 'Tour' },
                { value: 'TRANSFER_VEHICLE', label: 'Transfer' },
                { value: 'FLIGHT_SEAT', label: 'Flight' },
              ]}
              value={typeFilter}
              onValueChange={setTypeFilter}
              placeholder="Filter by type"
              searchPlaceholder="Search..."
            />
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">{t('loading')}</span>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && allocations.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">
                {t('empty.noAllocations') ||
                  'No available allocations found for these dates'}
              </p>
            </div>
          )}

          {/* Allocations List */}
          {!isLoading && allocations.length > 0 && (
            <>
              {/* Select All */}
              <div className="flex items-center justify-between border-b pb-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-sm text-primary hover:underline"
                >
                  {selectedIds.size === allocations.length
                    ? 'Deselect All'
                    : 'Select All'}
                </button>
                <span className="text-sm text-muted-foreground">
                  {selectedIds.size} selected
                </span>
              </div>

              <div className="space-y-2 overflow-y-auto flex-1 max-h-[350px] pr-1">
                {allocations.map((allocation) => {
                  const Icon =
                    typeIcons[allocation.allocationType as AllocationType];
                  const isSelected = selectedIds.has(allocation.id);

                  return (
                    <div
                      key={allocation.id}
                      onClick={() => toggleSelection(allocation.id)}
                      className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        // The row itself toggles, so the checkbox must stop the
                        // event bubbling or the selection toggles twice and
                        // never changes. (The primitive owns the button's
                        // onClick, so this has to happen via onCheckedChange.)
                        onCheckedChange={(_checked, event) => {
                          event?.stopPropagation?.();
                          toggleSelection(allocation.id);
                        }}
                      />
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                          typeColors[allocation.allocationType as AllocationType]
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{allocation.name}</p>
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                              typeColors[
                                allocation.allocationType as AllocationType
                              ]
                            }`}
                          >
                            {typeLabels[
                              allocation.allocationType as AllocationType
                            ] || allocation.allocationType}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {allocation.partner.companyName}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-green-600">
                          {allocation.availableUnits}{' '}
                          {t('fields.available') || 'available'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: allocation.currency,
                          }).format(allocation.unitCost)}
                          {' / '}
                          {tCommon('unit') || 'unit'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {tCommon('cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={selectedIds.size === 0}>
            <Check className="mr-2 h-4 w-4" />
            Add {selectedIds.size > 0 ? `(${selectedIds.size})` : ''} Allocations
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
